import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface FAQ {
  question: string;
  answer: string;
  category: 'general' | 'account' | 'projects' | 'payments';
}

const HelpCenter: React.FC = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqs: FAQ[] = [
    {
      question: "How do I create an account on NexSync?",
      answer: "Click on the 'Sign Up' button in the top right corner, select your role (Student, Freelancer, or Client), fill in your details, and verify your email. You'll receive a confirmation email with your unique ID.",
      category: 'account'
    },
    {
      question: "What types of projects can I post on NexSync?",
      answer: "You can post academic projects including SIWES reports, final year projects, research papers, thesis writing, presentations, and any other academic documentation. All projects must comply with academic integrity policies.",
      category: 'projects'
    },
    {
      question: "How are freelancers vetted on the platform?",
      answer: "All freelancers undergo a rigorous verification process including: identity verification, educational qualification checks, portfolio review, and initial skill assessment tests. Only approved freelancers can accept projects.",
      category: 'general'
    },
    {
      question: "What payment methods are accepted?",
      answer: "We accept bank transfers, USSD, debit cards, and mobile money. All payments are secured through our escrow system and only released when you approve the completed work.",
      category: 'payments'
    },
    {
      question: "How long does project approval take?",
      answer: "Project approval typically takes 24-48 hours. Our team reviews each project to ensure it meets quality standards and matches with appropriate freelancers. You'll receive email notifications throughout the process.",
      category: 'projects'
    },
    {
      question: "Can I get a refund if I'm not satisfied?",
      answer: "Yes, we have a satisfaction guarantee policy. If you're not satisfied with the delivered work, you can request revisions. If revisions don't meet your requirements, you can request a refund through our dispute resolution system.",
      category: 'payments'
    },
    {
      question: "How do I communicate with my assigned freelancer?",
      answer: "Once a freelancer accepts your project, you can communicate through our secure messaging system. We recommend keeping all communication on the platform for security and dispute resolution purposes.",
      category: 'general'
    },
    {
      question: "What security measures protect my data?",
      answer: "We use end-to-end encryption for all communications, secure payment gateways, regular security audits, and comply with data protection regulations. Your project details and personal information are never shared with third parties.",
      category: 'account'
    },
    {
      question: "How do I report inappropriate behavior?",
      answer: "Use the 'Report' button on any message or user profile, or contact our support team immediately. We take all reports seriously and will investigate promptly, taking appropriate action including account suspension if needed.",
      category: 'general'
    },
    {
      question: "What happens if my freelancer misses the deadline?",
      answer: "Freelancers are penalized for missed deadlines. You can request an extension or cancel the project for a full refund. Our project managers monitor timelines and will intervene if there are delays.",
      category: 'projects'
    }
  ];

  const categories = [
    { id: 'all', name: 'All Questions', count: faqs.length },
    { id: 'general', name: 'General', count: faqs.filter(f => f.category === 'general').length },
    { id: 'account', name: 'Account', count: faqs.filter(f => f.category === 'account').length },
    { id: 'projects', name: 'Projects', count: faqs.filter(f => f.category === 'projects').length },
    { id: 'payments', name: 'Payments', count: faqs.filter(f => f.category === 'payments').length }
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          <div className="navbar-actions">
            <button 
              onClick={() => navigate('/')}
              className="btn btn-ghost"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/signin')}
              className="btn btn-ghost"
            >
              Sign in
            </button>
            <button 
              onClick={() => navigate('/signup')}
              className="btn btn-primary"
            >
              Sign up
            </button>
          </div>
        </div>
      </nav>

      {/* Help Center Content */}
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Find answers to common questions about using NexSync. Can't find what you're looking for? Contact our support team.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help..."
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <svg className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                activeCategory === category.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>

        {/* Quick Help Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Getting Started</h3>
            <p className="text-gray-600 mb-4">
              New to NexSync? Learn how to set up your account and post your first project.
            </p>
            <button className="text-blue-600 font-medium hover:text-blue-700">
              View Guide →
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Payments & Billing</h3>
            <p className="text-gray-600 mb-4">
              Information about payment methods, billing, and our escrow system.
            </p>
            <button className="text-green-600 font-medium hover:text-green-700">
              Learn More →
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Safety & Security</h3>
            <p className="text-gray-600 mb-4">
              Learn how we protect your data and ensure safe transactions on our platform.
            </p>
            <button className="text-purple-600 font-medium hover:text-purple-700">
              Read More →
            </button>
          </div>
        </div>

        {/* FAQs Section */}
        <div className="bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
          
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-medium text-gray-900">{faq.question}</span>
                  <svg
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      expandedFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {expandedFAQ === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700">{faq.answer}</p>
                    <div className="mt-3">
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
                        {faq.category.charAt(0).toUpperCase() + faq.category.slice(1)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Contact Support Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Need Help?</h2>
            <p className="text-lg text-gray-600 mb-8">
              Our support team is available 24/7 to assist you with any issues or questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/contactus')}
                className="btn btn-primary btn-lg"
              >
                Contact Support
              </button>
              <button 
                onClick={() => navigate('/documentation')}
                className="btn btn-secondary btn-lg"
              >
                View Documentation
              </button>
            </div>
            
            <div className="mt-8 grid md:grid-cols-3 gap-6 text-left">
              <div className="text-center">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-900">Phone Support</h4>
                <p className="text-gray-600">+234 800 123 4567</p>
                <p className="text-sm text-gray-500">Mon-Fri, 9AM-6PM</p>
              </div>
              
              <div className="text-center">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-900">Email Support</h4>
                <p className="text-gray-600">support@nexsync.com</p>
                <p className="text-sm text-gray-500">24/7 Response</p>
              </div>
              
              <div className="text-center">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h4 className="font-semibold text-gray-900">Live Chat</h4>
                <p className="text-gray-600">Chat with us</p>
                <p className="text-sm text-gray-500">Available now</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="navbar-brand">
                <div className="brand-logo">NS</div>
                <span className="brand-text">NexSync</span>
              </div>
              <p className="footer-description">
                Your trusted partner for academic project success.
              </p>
            </div>
            
            <div className="footer-links">
              <div className="footer-column">
                <h4>Support</h4>
                <button onClick={() => navigate('/helpcenter')} className="footer-link">Help Center</button>
                <button onClick={() => navigate('/contactus')} className="footer-link">Contact Us</button>
                <button onClick={() => navigate('/documentation')} className="footer-link">Documentation</button>
              </div>
              
              <div className="footer-column">
                <h4>Legal</h4>
                <button onClick={() => navigate('/privacypolicy')} className="footer-link">Privacy Policy</button>
                <button onClick={() => navigate('/termsofservice')} className="footer-link">Terms of Service</button>
                <button onClick={() => navigate('/cookiepolicy')} className="footer-link">Cookie Policy</button>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NexSync. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenter;